import { BigNumber } from "shared/cubiq-model-multi-asset";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxLabelHelper } from "../../../../bfx-common/helpers/bfx-label-helper";

/* AUTOCOMPLETE MODELS */

// optional default generic since 2.2
export class ICAutocompleteGenericModel<T = any> {
  // must
  public value: T;
  public label: string;
  public lastFavorite: boolean;

  public hasPrivatization: boolean = false;

  constructor(_value?: any, _label?: string, _hasPrivatization?: boolean) {
    this.value = _value != null ? _value : null;
    this.label = _label != null ? _label : null;
    this.lastFavorite = null;
    this.hasPrivatization = _hasPrivatization;
  }
}

export class ICAutocompleteAccountModel {
  // must
  public value: string;
  public label: string;
  public smartLabel: string;
  // model
  public id: string;
  public name: string;
  public bookingCode: string;
  public contact: string;
  //public isAutoFillOrBreak: boolean = false; // no longer valid with client account m2m mapping
  public lastFavorite: boolean;

  public hasPrivatization: boolean = false;

  // keep default true
  public hasFullVisibility: boolean = true;

  constructor(
    _value?: string,
    _label?: string,
    _id?: string,
    _name?: string,
    _bookingCode?: string,
    _contact?: string,
    _hasPrivatization?: boolean,
    _hasFullVisibility?: boolean,
    //_isAutoFillOrBreak?:boolean,
    _smartLabel?: string,
  ) {
    this.value = _value ? _value : null;
    this.label = _label ? _label : null;
    this.id = _id ? _id : null;
    this.name = _name ? _name : null;
    this.bookingCode = _bookingCode ? _bookingCode : null;
    this.contact = _contact ? _contact : null;
    this.lastFavorite = null;
    this.hasPrivatization = _hasPrivatization;
    this.smartLabel = _smartLabel ? _smartLabel : null;

    if (_hasFullVisibility === true || _hasFullVisibility === false)
      this.hasFullVisibility = _hasFullVisibility;

    //this.isAutoFillOrBreak = (_isAutoFillOrBreak != null ? _isAutoFillOrBreak : null);
  }
}

export class ICAutocompleteClientModel {
  // must
  public value: string;
  public label: string;
  // model
  public id: string;
  public name: string;
  public bookingCode: string;
  public lastFavorite: boolean;
  public smartLabel: string;
  public hasPrivatization: boolean = false;

  public isAutoFillEnabled: boolean = false;

  public branchCd: string = null;

  constructor(
    _value?: string,
    _label?: string,
    _id?: string,
    _name?: string,
    _hasPrivatization?: boolean,
    _isAutoFillEnabled?: boolean,
    _branchCd?: string,
  ) {
    this.value = _value ? _value : null;
    this.label = _label ? _label : null;
    this.id = _id ? _id : null;
    this.name = _name ? _name : null;
    this.lastFavorite = null;
    this.hasPrivatization = _hasPrivatization;
    this.isAutoFillEnabled = typeof _isAutoFillEnabled === "boolean" ? _isAutoFillEnabled : false;
    this.branchCd = _branchCd != null ? _branchCd : null;
  }
}

export class ICAutocompleteTabModel {
  // must
  public tab: string;
  public label: string;
  public active: boolean;
  public disabled: boolean;

  constructor(_tab?: string, _label?: string, _active?: boolean, _disabled?: boolean) {
    this.tab = _tab ? _tab : null;
    this.label = _label ? _label : null;
    this.active = _active ? _active : null;
    this.disabled = _disabled ? _disabled : null;
  }
}

/* OTHER MODELS */
export class ICFixedCcyModel {
  // must
  public ccy: string; // 1 = base, 0 = non-base, null = '-'
  public ccyPair: string;
  constructor(_ccy: string = null, _ccyPair: string = null) {
    this.ccy = _ccy ? _ccy : null;
    this.ccyPair = _ccyPair ? _ccyPair : null;
  }
}

export class ICMarkupModel {
  public value: BigNumber;
  public textValue: string;
  public typeValue: ICAutocompleteGenericModel;
  public checkbox: { enabled: boolean; value: boolean } = {
    enabled: true,
    value: false,
  };
  /** client reverseEngineer setting should be preferred.
   *  this is only useful when modifying private orders */
  public reverseEngineer: boolean = false;
  constructor() {}
}

export class ICSuspendModel {
  suspendFrom: ICTimeControlModel;
  suspendTo: ICTimeControlModel;
  activated: boolean; // state of the checkbox
  constructor() {}
}

export class ICTimeControlModel {
  constructor(
    public timeValue?: string,
    public timezoneValue?: ICAutocompleteGenericModel,
    public timezoneModel?: CubiqTS.TimeZoneImpl,
  ) {}
}

export class ICCommentsModel {
  public value: string;
  public manualComment: string;
  public defaultComment: string;
  public clientTraderComment: string;

  constructor() {}
}

export class ICTenorValueDateModel {
  private _value: any;
  public get value(): any {
    return this._value;
  }
  public set value(value: any) {
    this._value = value;
  }
  private _label: string;
  public get label(): string {
    return this._label;
  }
  public set label(value: string) {
    this._label = value;
  }
  public tenor: CubiqTS.Tenor; // 1 = base, 0 = non-base, null = '-'
  public valueDate: Date;
  public fixingDate: Date; // used for ndf's
  constructor(_tenor: CubiqTS.Tenor, _valueDate?: Date, _fixingDate?: Date) {
    this.tenor = _tenor ? _tenor : null;
    this.valueDate = _valueDate ? _valueDate : null;
    this.fixingDate = _fixingDate ? _fixingDate : null;
    if (this.tenor) {
      this.label = BfxLabelHelper.getTenorLabel(_tenor);
    } else if (this.valueDate) {
      this.label = CubiqTS.moment(this.valueDate).format("DD-MMM-YYYY");
    }

    if ((_tenor == undefined || _tenor == CubiqTS.Tenor.EMPTY) && _valueDate != undefined) {
      this.value = this.valueDate;
    } else if (_tenor != undefined) {
      this.value = this.tenor;
    }
  }
}

export class ICFixingSourceModel {
  // must
  public value: string;
  public label: string;
  // model
  public id: number;
  public pair: string;
  public lastFavorite: boolean;

  public isDefault: boolean = false;
  public isCustom: boolean = false;

  constructor(
    _value?: string,
    _label?: string,
    _id?: number,
    _pair?: string,
    _isDefault?: boolean,
    _isCustom?: boolean,
  ) {
    this.value = _value ? _value : null;
    this.label = _label ? _label : null;
    this.id = _id ? _id : null;
    this.pair = _pair ? _pair : null;
    this.lastFavorite = null;
    this.isDefault = _isDefault;
    this.isCustom = _isCustom;
  }
}
